export const THEME_STORAGE_KEY = 'sn-theme'
export const DEFAULT_THEME = 'light'

import { getDecadeEraColors, getDecadeYearBreaks } from '../lib/periodGroups.js'

export function getSystemTheme() {
  if (typeof window === 'undefined') return DEFAULT_THEME
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export function hasStoredThemePreference() {
  if (typeof window === 'undefined') return false
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY)
  return stored === 'light' || stored === 'dark'
}

export function getPreferredTheme() {
  if (typeof window === 'undefined') return DEFAULT_THEME
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY)
  if (stored === 'light' || stored === 'dark') return stored
  return getSystemTheme()
}

export function applyDocumentTheme(theme) {
  if (typeof document === 'undefined') return
  document.documentElement.dataset.theme = theme
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) {
    meta.setAttribute('content', theme === 'light' ? '#f5f5f5' : '#111111')
  }
}

export function persistThemePreference(theme) {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(THEME_STORAGE_KEY, theme)
}

/** OpenFreeMap vector styles (no API key). Positron ≈ Carto light; dark is Dark Matter on OFM tiles. */
const PUBLIC_BASE_URL = import.meta.env.BASE_URL ?? '/'

export const BASEMAP_STYLE_URLS = {
  dark: `${PUBLIC_BASE_URL}basemaps/openfreemap-dark-matter.json`,
  light: 'https://tiles.openfreemap.org/styles/positron',
}

export const BASEMAP_ATTRIBUTION =
  '<a href="https://openfreemap.org">OpenFreeMap</a> <a href="https://www.openmaptiles.org/">&copy; OpenMapTiles</a> <a href="https://www.openstreetmap.org/copyright">&copy; OpenStreetMap</a>'

export function getBasemapStyleUrl(theme) {
  return BASEMAP_STYLE_URLS[theme] ?? BASEMAP_STYLE_URLS.light
}

export const MAP_LABEL_COLORS = {
  dark: {
    text: '#f3fbff',
    year: '#a8b4c0',
    halo: 'rgba(0, 2, 8, 0.98)',
    haloWidth: 2.75,
    haloBlur: 0.5,
  },
  light: {
    text: '#1a2332',
    year: '#5c6573',
    halo: 'rgba(255, 255, 255, 0.95)',
    haloWidth: 2.5,
    haloBlur: 0.5,
  },
}

/** OpenFreeMap glyph stacks (Noto Sans Regular / Bold / Italic). */
export const ROAD_LABEL_LAYER_FONT = ['Noto Sans Bold', 'Noto Sans Regular']

export const MAP_BACKGROUND_COLORS = {
  dark: '#121212',
  light: '#e8e8e8',
}

/** Road palette; era colours are generated per decade in periodGroups.js */
export const MAP_ROAD_PALETTE = {
  dark: {
    unknown: '#8a93a8',
    highlightGlow: '#e8e8e8',
    highlightCore: '#f0f0f0',
    focus: '#9a9a9a',
    opacity: {
      unknown: 0.52,
      fadeMin: 0.5,
      fadeMid: 0.68,
      fadeMax: 0.98,
      dimMultiplier: 0.38,
      labelUnknown: 0.82,
      labelFadeMin: 0.68,
      labelFadeMid: 0.8,
      labelFadeMax: 0.98,
      labelDimMultiplier: 0.7,
      selectedLabelOpacity: 1,
    },
  },
  light: {
    unknown: '#9aa3b0',
    highlightGlow: '#ffffff',
    highlightCore: '#1a2332',
    focus: '#4a5568',
    opacity: {
      unknown: 0.45,
      fadeMin: 0.38,
      fadeMid: 0.62,
      fadeMax: 1,
      dimMultiplier: 0.45,
      labelUnknown: 0.85,
      labelFadeMin: 0.72,
      labelFadeMid: 0.8,
      labelFadeMax: 1,
      labelDimMultiplier: 0.65,
      selectedLabelOpacity: 1,
    },
  },
}

/** MapLibre expression: map display year (built-first), or -1 when no year. to-number(null) is 0, not missing. */
export function buildMapYearExpr() {
  const mapYear = [
    'case',
    ['any', ['!', ['has', 'map_year']], ['==', ['get', 'map_year'], null]],
    -1,
    ['coalesce', ['to-number', ['get', 'map_year']], -1],
  ]
  return [
    'case',
    ['!=', mapYear, -1],
    mapYear,
    [
      'case',
      ['any', ['!', ['has', 'naming_year']], ['==', ['get', 'naming_year'], null]],
      -1,
      ['coalesce', ['to-number', ['get', 'naming_year']], -1],
    ],
  ]
}

/** @deprecated Use buildMapYearExpr — kept for callers not yet migrated. */
export function buildNamingYearExpr() {
  return buildMapYearExpr()
}

export function getRoadPalette(theme) {
  return MAP_ROAD_PALETTE[theme] ?? MAP_ROAD_PALETTE.dark
}

export function buildRoadLineColorPaint(theme) {
  const { unknown } = getRoadPalette(theme)
  const eras = getDecadeEraColors(theme)
  const breaks = getDecadeYearBreaks()
  const mapYear = buildMapYearExpr()
  return [
    'case',
    ['==', mapYear, -1],
    unknown,
    ['step', mapYear, eras[0], ...breaks.flatMap((year, index) => [year, eras[index + 1]])],
  ]
}

export function getThemedLegendColor(group, theme) {
  const palette = getRoadPalette(theme)
  if (group.isUnknown) return palette.unknown
  if (Number.isInteger(group.colorIndex)) {
    return getDecadeEraColors(theme)[group.colorIndex]
  }
  return palette.unknown
}
